import classNames from "classnames";

import { ITile } from "../../../store/slices/environmentSlice";
import SvgIcon from "../SvgIcon";

import styles from "./index.module.scss";

interface ICustomTilesProps {
  tiles: ITile[];
}

function renderInlineContent(text: string): (string | JSX.Element)[] {
  const parts: (string | JSX.Element)[] = [];
  const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    parts.push(
      <a key={match.index} href={match[2]} target="_blank" rel="noreferrer">
        {match[1]}
      </a>,
    );
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

function renderSection(lines: string[], key: number): JSX.Element {
  const groups: { type: "bullet" | "text"; lines: string[] }[] = [];

  for (const line of lines) {
    const isBullet = line.startsWith("- ");
    const last = groups[groups.length - 1];

    if (last && last.type === (isBullet ? "bullet" : "text")) {
      last.lines.push(isBullet ? line.replace(/^-\s*/, "") : line);
    } else {
      groups.push({
        type: isBullet ? "bullet" : "text",
        lines: [isBullet ? line.replace(/^-\s*/, "") : line],
      });
    }
  }

  return (
    <div key={key} className={styles.tile_contentSection}>
      {groups.map((group, gi) =>
        group.type === "bullet" ? (
          <ul key={gi} className={styles.tile_contentList}>
            {group.lines.map((line, li) => (
              <li key={li}>{renderInlineContent(line)}</li>
            ))}
          </ul>
        ) : (
          <div key={gi} className={styles.tile_contentText}>
            {group.lines.map((line, li) => (
              <p key={li}>{renderInlineContent(line)}</p>
            ))}
          </div>
        ),
      )}
    </div>
  );
}

function renderContent(content: string): JSX.Element {
  const sections = content
    .split("\n\n")
    .map((s) => s.split("\n").filter(Boolean))
    .filter((s) => s.length > 0);

  return <>{sections.map((section, si) => renderSection(section, si))}</>;
}

function CustomTiles({ tiles = [] }: ICustomTilesProps) {
  const getTileValue = (value: string | (() => string)): string => {
    return typeof value === "function" ? value() : value;
  };

  return (
    <div className={styles.tiles}>
      {tiles.map((tile, index) => {
        const title = getTileValue(tile.title);
        const description = getTileValue(tile.description_text);
        const backgroundImage = getTileValue(tile.background_image);
        const isClickable = !!tile.link_url && !tile.content;

        const tileInner = (
          <>
            <div className={styles.tile_bottom}>
              <div className={styles.tile_text}>
                <p className={styles.tile_title}>{title}</p>
                {tile.content ? (
                  <div className={styles.tile_content}>
                    {renderContent(tile.content)}
                  </div>
                ) : (
                  <p className={styles.tile_description}>{description}</p>
                )}
              </div>

              {isClickable && (
                <div className={styles.tile_playContainer}>
                  <SvgIcon type="FiPlayCircle" />
                </div>
              )}
            </div>

            <div
              className={classNames(
                styles.tile_bg,
                styles[`tile_bg_${tile.background_color}`],
              )}
              style={{
                backgroundColor: tile.background_color,
                opacity: 0.65,
              }}
            />
          </>
        );

        if (isClickable) {
          return (
            <a
              key={`${title}_${index}`}
              href={tile.link_url}
              target={tile.target || "_self"}
              rel={tile.target === "_blank" ? "noreferrer" : undefined}
              className={styles.tile}
              style={{
                background: `url(${backgroundImage}) center/cover`,
                textDecoration: "none",
              }}>
              {tileInner}
            </a>
          );
        }

        return (
          <div
            key={`${title}_${index}`}
            className={classNames(styles.tile, styles.tile_static)}
            style={{
              background: `url(${backgroundImage}) center/cover`,
            }}>
            {tileInner}
          </div>
        );
      })}
    </div>
  );
}

export default CustomTiles;
